import { ICalendarSubjects, IOfferedSubject, ISubjectColors } from "../types";

export const getInitialHour = (data: any[]) => {
  return data.reduce((acc, e) => {
    return e.desde < acc ? e.desde : acc;
  }, Infinity);
};

export const getFinalHour = (data: any[]) => {
  return data.reduce((acc, e) => {
    return e.hasta > acc ? e.hasta : acc;
  }, 0);
};

export const getRowIndex = (time: number, initialTime: number) => {
  const hour = Math.floor(time / 100);
  const minute = time % 100;
  const initialHour = Math.floor(initialTime / 100);
  const totalMinutes = (hour - initialHour) * 60 + minute;
  return totalMinutes / 5 + 1;
};

export const WEEK_DAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"] as const;

export const abbreviateTranslatedDay = (translated: string, original: string, length = 2) => {
  const normalized = translated.trim();
  const stillSpanish = WEEK_DAY_NAMES.some(
    (nombre) => nombre.toLocaleLowerCase("es") === normalized.toLocaleLowerCase("es")
  );
  return stillSpanish ? original.slice(0, length) : normalized.slice(0, length);
};

export const getGridColumn = (dia: string) => {
  switch (dia) {
    case "Lunes":
      return 2;
    case "Martes":
      return 3;
    case "Miércoles":
      return 4;
    case "Jueves":
      return 5;
    case "Viernes":
      return 6;
    default:
      return 2;
  }
};

const colors: ISubjectColors[] = [
  { background: "#2277DA", border: "#2277DA" },
  { background: "#FF4789", border: "#FF4789" },
  { background: "#FF7A30", border: "#FF7A30" },
  { background: "#FBC54F", border: "#FBC54F" },
  { background: "#97D86C", border: "#97D86C" },
  { background: "#2ED4C6", border: "#2ED4C6" },
  { background: "#C47DDD", border: "#C47DDD" },
];

const colorMap = new Map();

export const assignColorToSubject = (idcurso: number, anioLectivo: number) => {
  const key = `${idcurso}-${anioLectivo}`;
  if (!colorMap.has(key)) {
    const colorIndex = colorMap.size % colors.length;
    colorMap.set(key, colors[colorIndex]);
  }

  return colorMap.get(key);
};

export const checkIfTimeOverlaps = (initial1: number, end1: number, initial2: number, end2: number) => {
  // return initial1 < end2 && initial2 < end1
  return (
    (initial1 >= initial2 && initial1 < end2) ||
    (initial2 > initial2 && initial2 <= end2) ||
    (initial1 <= initial2 && initial2 >= end2)
  );
};

const generateCourseDictionary = (subjects: ICalendarSubjects[]) => {
  const courseDictionary: Record<string, number> = {};
  let currentNumber = 0;

  subjects.forEach((subject) => {
    const key = `${subject.idCurso}-${subject.anioLectivo}`;
    if (!(key in courseDictionary)) {
      courseDictionary[key] = currentNumber;
      currentNumber++;
    }
  });

  return courseDictionary;
};

function calcularDuracionEnMinutos(horaInicio: number, horaFin: number): number {
  // Obtener las horas y los minutos de la hora de inicio
  const horasInicio = Math.floor(horaInicio / 100);
  const minutosInicio = horaInicio % 100;

  // Obtener las horas y los minutos de la hora de fin
  const horasFin = Math.floor(horaFin / 100);
  const minutosFin = horaFin % 100;

  // Convertir todo a minutos
  const inicioEnMinutos = horasInicio * 60 + minutosInicio;
  const finEnMinutos = horasFin * 60 + minutosFin;

  // Calcular la diferencia
  const duracionEnMinutos = finEnMinutos - inicioEnMinutos;
  return duracionEnMinutos;
}

export const tieneHorarioAsignado = (curso: Partial<ICalendarSubjects>): boolean => {
  const dia = curso.dia?.trim();
  const desde = Number(curso.desde);
  const hasta = Number(curso.hasta);

  return Boolean(dia) && !Number.isNaN(desde) && !Number.isNaN(hasta) && hasta > desde;
};

export function asignarSuperposiciones(cursos: ICalendarSubjects[], esCarreraDeGrado: boolean | null): ICalendarSubjects[] {
  if (!cursos || cursos.length === 0) return [];

  const resultado: ICalendarSubjects[] = cursos.map((curso) => ({
    ...curso,
    superpuesta: 0,
    warning: false,
    tieneSuperposicion: false,
    zIndex: undefined,
  }));

  const grupos: number[][] = [];

  for (let i = 0; i < resultado.length; i++) {
    if (!resultado[i]) continue;

    for (let j = i + 1; j < resultado.length; j++) {
      if (!resultado[j]) continue;

      const cursoA = resultado[i];
      const cursoB = resultado[j];

      const desdeA = Number(cursoA.desde);
      const hastaA = Number(cursoA.hasta);
      const desdeB = Number(cursoB.desde);
      const hastaB = Number(cursoB.hasta);

      let seSuperponen = false;

      if (
        (desdeA === desdeB && hastaA === hastaB) ||
        (desdeA >= desdeB && desdeA < hastaB) ||
        (hastaA > desdeB && hastaA <= hastaB) ||
        (desdeA <= desdeB && hastaA >= hastaB)
      ) {
        seSuperponen = true;
      }

      if (seSuperponen) {
        let grupoEncontrado = false;
        for (const grupo of grupos) {
          if (grupo.includes(i) || grupo.includes(j)) {
            if (!grupo.includes(i)) grupo.push(i);
            if (!grupo.includes(j)) grupo.push(j);
            grupoEncontrado = true;
            break;
          }
        }

        if (!grupoEncontrado) {
          grupos.push([i, j]);
        }
      }
    }
  }

  let cambio = true;
  while (cambio) {
    cambio = false;
    for (let i = 0; i < grupos.length; i++) {
      for (let j = i + 1; j < grupos.length; j++) {
        const tienenInterseccion = grupos[i].some((elem) => grupos[j].includes(elem));

        if (tienenInterseccion) {
          const grupoFusionado: number[] = [...grupos[i]];

          for (const idx of grupos[j]) {
            if (!grupoFusionado.includes(idx)) {
              grupoFusionado.push(idx);
            }
          }
          grupos[i] = grupoFusionado;
          grupos.splice(j, 1);
          cambio = true;
          break;
        }
      }
      if (cambio) break;
    }
  }

  grupos.forEach((grupo) => {
    if (grupo.length <= 1) return;

    let cursoPrincipalPos = 0;
    for (let i = 1; i < grupo.length; i++) {
      if (Number(resultado[grupo[i]].duracion) > Number(resultado[grupo[cursoPrincipalPos]].duracion)) {
        cursoPrincipalPos = i;
      }
    }

    const idxCursoPrincipal = grupo[cursoPrincipalPos];
    resultado[idxCursoPrincipal].superpuesta = 1;

    const superposicionesEnGrupo: Record<number, number[]> = {};
    const duracionSuperposicion: Record<string, number> = {};

    const cursosConSuperposicionFechas: number[] = [];

    const superposicionesFechas: Record<number, number[]> = {};
    grupo.forEach(idx => {
      superposicionesFechas[idx] = [];
    });

    const cursosPosgradoSinSuperposicionFechas: number[] = [];

    for (const idxA of grupo) {
      superposicionesEnGrupo[idxA] = [];

      for (const idxB of grupo) {
        if (idxA === idxB) continue;

        const cursoA = resultado[idxA];
        const cursoB = resultado[idxB];

        const desdeA = Number(cursoA.desde);
        const hastaA = Number(cursoA.hasta);
        const desdeB = Number(cursoB.desde);
        const hastaB = Number(cursoB.hasta);

        if (
          (desdeA === desdeB && hastaA === hastaB) ||
          (desdeA >= desdeB && desdeA < hastaB) ||
          (hastaA > desdeB && hastaA <= hastaB) ||
          (desdeA <= desdeB && hastaA >= hastaB)
        ) {
          superposicionesEnGrupo[idxA].push(idxB);

          const inicioSuperposicion = Math.max(desdeA, desdeB);
          const finSuperposicion = Math.min(hastaA, hastaB);
          const duracion = finSuperposicion - inicioSuperposicion;

          const key = `${Math.min(idxA, idxB)}-${Math.max(idxA, idxB)}`;
          duracionSuperposicion[key] = duracion;

          if (
            !esCarreraDeGrado &&
            cursoA.infoFechas &&
            cursoB.infoFechas &&
            Number(cursoA.anioLectivo) === Number(cursoB.anioLectivo)
          ) {
            const fechaInicioA = new Date(cursoA.infoFechas.fechaComienzo || '');
            const fechaFinA = new Date(cursoA.infoFechas.fechaFin || '');
            const fechaInicioB = new Date(cursoB.infoFechas.fechaComienzo || '');
            const fechaFinB = new Date(cursoB.infoFechas.fechaFin || '');

            if (fechaInicioA && fechaFinA && fechaInicioB && fechaFinB) {

              if (fechaInicioA <= fechaFinB && fechaInicioB <= fechaFinA) {

                superposicionesFechas[idxA].push(idxB);

                if (!cursosConSuperposicionFechas.includes(idxA)) {
                  cursosConSuperposicionFechas.push(idxA);
                }
                if (!cursosConSuperposicionFechas.includes(idxB)) {
                  cursosConSuperposicionFechas.push(idxB);
                }
              } else {

                if (!cursosPosgradoSinSuperposicionFechas.includes(idxA)) {
                  cursosPosgradoSinSuperposicionFechas.push(idxA);
                }
                if (!cursosPosgradoSinSuperposicionFechas.includes(idxB)) {
                  cursosPosgradoSinSuperposicionFechas.push(idxB);
                }
              }
            }
          }
        }
      }
    }

    cursosConSuperposicionFechas.forEach(idx => {
      resultado[idx].tieneSuperposicion = true;
    });

    const asignarNiveles = (idx: number, nivel: number, visitados: Set<number> = new Set()) => {
      if (visitados.has(idx) || resultado[idx].superpuesta >= nivel) return;

      visitados.add(idx);

      resultado[idx].superpuesta = nivel;

      superposicionesEnGrupo[idx]?.forEach((otroIdx) => {
        if (otroIdx !== idxCursoPrincipal) {
          asignarNiveles(otroIdx, nivel + 1, visitados);
        }
      });
    };

    superposicionesEnGrupo[idxCursoPrincipal]?.forEach((idx) => {
      asignarNiveles(idx, 2, new Set());
    });

    let maxNivelGrupo = 0;
    for (const idx of grupo) {
      if (resultado[idx].superpuesta > maxNivelGrupo) {
        maxNivelGrupo = resultado[idx].superpuesta;
      }
    }

    if (!esCarreraDeGrado) {
      const now = new Date();

      const cercaniaInfo = grupo.map(idx => {
        const curso = resultado[idx];
        let fechaInicio = null;

        if (curso.infoFechas && curso.infoFechas.fechaComienzo) {
          fechaInicio = new Date(curso.infoFechas.fechaComienzo);
        }

        const cercania = fechaInicio ? Math.abs(fechaInicio.getTime() - now.getTime()) : Infinity;

        return {
          idx,
          cercania,
          esPrincipal: idx === idxCursoPrincipal,
          tieneSuperposicion: resultado[idx].tieneSuperposicion
        };
      });

      cercaniaInfo.sort((a, b) => {
        if (a.cercania === b.cercania) {
          return a.esPrincipal ? -1 : (b.esPrincipal ? 1 : 0);
        }
        return a.cercania - b.cercania;
      });

      cercaniaInfo.forEach((info, index) => {
        resultado[info.idx].zIndex = cercaniaInfo.length - index;
      });

      if (cursosConSuperposicionFechas.length > 0) {

        const cursosConSuperp = cercaniaInfo.filter(info => 
          cursosConSuperposicionFechas.includes(info.idx)
        );

        if (cursosConSuperp.length > 0) {

          cursosConSuperp.sort((a, b) => {
            const zIndexA = resultado[a.idx].zIndex || 0;
            const zIndexB = resultado[b.idx].zIndex || 0;
            return zIndexB - zIndexA; 
          });

          const cursoMayorZIndex = cursosConSuperp[0].idx;
          resultado[cursoMayorZIndex].warning = true;
        }
      }
    } else {

      // Marca tieneSuperposicion solo cuando hay par del mismo año lectivo en el grupo
      grupo.forEach((idx) => {
        const cursoActual = resultado[idx];
        const tienePareMismoAnio = grupo.some(
          (idxB) =>
            idxB !== idx &&
            Number(resultado[idxB].anioLectivo) === Number(cursoActual.anioLectivo)
        );
        if (tienePareMismoAnio) {
          resultado[idx].tieneSuperposicion = true;
        }
      });

      grupo.forEach((idxA) => {
        if (idxA === idxCursoPrincipal) return;

        const cursoA = resultado[idxA];
        const duracionA = Number(cursoA.duracion);

        // Solo se evalúa warning entre cursos del mismo año lectivo
        const paresMismoAnio = grupo.filter(
          (idxB) =>
            idxB !== idxA &&
            Number(resultado[idxB].anioLectivo) === Number(cursoA.anioLectivo)
        );

        if (paresMismoAnio.length === 0) {
          resultado[idxA].warning = false;
          return;
        }

        let maxNivelMismoAnio = resultado[idxA].superpuesta;
        paresMismoAnio.forEach((idxB) => {
          if (resultado[idxB].superpuesta > maxNivelMismoAnio) {
            maxNivelMismoAnio = resultado[idxB].superpuesta;
          }
        });

        let debeRecibir = resultado[idxA].superpuesta === maxNivelMismoAnio;

        paresMismoAnio.forEach((idxB) => {
          const key = `${Math.min(idxA, idxB)}-${Math.max(idxA, idxB)}`;
          const duracionSuperp = duracionSuperposicion[key] || 0;

          if (duracionSuperp > duracionA * 0.5) {
            debeRecibir = true;
          }
        });

        resultado[idxA].warning = debeRecibir;
      });
    }
  });

  return resultado;
}
export function asignacionPropiedadesParseoCursos(cursos: ICalendarSubjects[]): ICalendarSubjects[] {
  const cursosConHorario = cursos.filter(tieneHorarioAsignado);
  if (cursosConHorario.length === 0) return [];

  /* -------------------------------------------------------------------------- */
  /*                               Setear COLORES                               */
  /* -------------------------------------------------------------------------- */
  const colors = [
    "#FF4789",
    "#FF7A30",
    "#FBC54F",
    "#97D86C",
    "#209DAE",
    "#2277DA",
    "#C47DDD",
    "#FF4789",
    "#FF7A30",
    "#FBC54F",
    "#97D86C",
    "#209DAE",
    "#2277DA",
    "#C47DDD",
  ];
  const diccionariocolors = generateCourseDictionary(cursosConHorario);
  cursosConHorario.map((item) => {
    const key = `${item.idCurso}-${item.anioLectivo}`;
    const color = colors[diccionariocolors[key] % colors.length];
    item.color = color;
  });

  /* ----------------------------- Agrupar por DIA ---------------------------- */
  const agrupadosPorDia: { [key: string]: ICalendarSubjects[] } = cursosConHorario.reduce(
    (acumulador: any, curso: ICalendarSubjects) => {
      const diaNormalizado = curso.dia!.trim().toLowerCase();
      if (!acumulador[diaNormalizado]) {
        acumulador[diaNormalizado] = [];
      }
      acumulador[diaNormalizado].push(curso);
      return acumulador;
    },
    {}
  );
  /* -------------------------------------------------------------------------- */
  /*                         Recorrer por DIA y PARSEAR                         */
  /* -------------------------------------------------------------------------- */
  let cursosParseados: ICalendarSubjects[] = [];
  Object.values(agrupadosPorDia).forEach((grupo: ICalendarSubjects[]) => {
    // Agregamos la propiedad Duracion
    grupo.forEach((curso) => (curso.duracion = calcularDuracionEnMinutos(curso.desde, curso.hasta)));
    // Ordernar por calcularDuracionEnMinutos 
    // @ts-ignore
    grupo.sort((a: ICalendarSubjects, b: ICalendarSubjects) => b.duracion - a.duracion);

    cursosParseados.push(...grupo)
  });

  return cursosParseados;
}

export const agruparItemCalendarioPorHorario = (cursos: ICalendarSubjects[]) => {
  const agrupados = new Map();
  cursos.forEach( c => {
    const key = `${c.idCurso}-${c.desde}-${c.hasta}`
    if (!agrupados.has(key)) {
      agrupados.set(key, { ...c });
    } else {
      const cursoExistente = agrupados.get(key)
      cursoExistente.tipoCursada = `${cursoExistente.tipoCursada}, ${c.tipoCursada}`
    }
  })

  return Array.from(agrupados.values())
}

export const verificarSuperposicionUltimoAgregado = (
  materiasCalendarizadas: ICalendarSubjects[],
  idUltimoAgregado: number,
  materiasOfrecidas: IOfferedSubject[] | null,
  esCarreraDeGrado: boolean | null
): boolean => {
  if (!idUltimoAgregado || !materiasOfrecidas) return false;

  let horariosNuevoCurso: { dia: string; desde: number; hasta: number }[] = [];
  let fechasNuevoCurso: { fechaComienzo: string; fechaFin: string } | null = null;
  let anioLectivoNuevoCurso: number | null = null;

  for (const materia of materiasOfrecidas) {
    const seccionEncontrada = materia.sections.find((seccion) => seccion.idCurso === idUltimoAgregado);
    if (seccionEncontrada) {

      anioLectivoNuevoCurso = Number(seccionEncontrada.anioLectivo);

      if (!esCarreraDeGrado) {

        const infoFechas = seccionEncontrada.infoFechas

        if (infoFechas && infoFechas.fechaComienzo && infoFechas.fechaFin) {
          fechasNuevoCurso = {
            fechaComienzo: infoFechas.fechaComienzo,
            fechaFin: infoFechas.fechaFin
          };
        }
      }

      if (seccionEncontrada.subjectData && seccionEncontrada.subjectData.subjectsByType) {
        seccionEncontrada.subjectData.subjectsByType.forEach((tipoMateria: any) => {
          if (tipoMateria.subjectInfo) {
            tipoMateria.subjectInfo.forEach((info: any) => {
              if (info.values && info.values.dia) {
                horariosNuevoCurso.push({
                  dia: info.values.dia,
                  desde: Number(info.values.desde),
                  hasta: Number(info.values.hasta),
                });
              }
            });
          }
        });
      }
      break;
    }
  }

  if (horariosNuevoCurso.length === 0) return false;

  for (const horario of horariosNuevoCurso) {

    const cursosSuperpuestos = materiasCalendarizadas.filter(
      (materia) =>
        materia.dia === horario.dia &&
        (anioLectivoNuevoCurso === null || Number(materia.anioLectivo) === anioLectivoNuevoCurso) &&
        ((materia.desde >= horario.desde && materia.desde < horario.hasta) ||
          (materia.hasta > horario.desde && materia.hasta <= horario.hasta) ||
          (materia.desde <= horario.desde && materia.hasta >= horario.hasta))
    );

    if (!esCarreraDeGrado && fechasNuevoCurso) {
      const fechaInicioNuevo = new Date(fechasNuevoCurso.fechaComienzo);
      const fechaFinNuevo = new Date(fechasNuevoCurso.fechaFin);

      const cursosConSuperposicionCompleta = cursosSuperpuestos.filter(curso => {
        if (!curso.infoFechas || !curso.infoFechas.fechaComienzo || !curso.infoFechas.fechaFin) {
          return false; 
        }

        const fechaInicioCurso = new Date(curso.infoFechas.fechaComienzo);
        const fechaFinCurso = new Date(curso.infoFechas.fechaFin);

        return fechaInicioNuevo <= fechaFinCurso && fechaInicioCurso <= fechaFinNuevo;
      });

      const idsSuperposicionCompleta = new Set(cursosConSuperposicionCompleta.map(curso => curso.idCurso));
      if (idsSuperposicionCompleta.size >= 2) { 
        return true;
      }
    } else {

      const idsUnicos = new Set(cursosSuperpuestos.map(curso => curso.idCurso));
      if (idsUnicos.size > 3) {
        return true;
      }
    }
  }

  return false;
};